import React, { useEffect, useState } from "react";
import { TrendingUp, Lightbulb } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { api } from "@/lib/api";

const COLORS = ["#ec4899", "#8b8b8b", "#3b82f6", "#f97316", "#a855f7", "#10b981", "#f59e0b", "#22d3ee"];

const Stat = ({ value, label, tint = "text-ink-primary" }) => (
  <div className="rounded-xl border border-line bg-bg-surface p-4 text-center">
    <div className={`font-display text-2xl font-bold ${tint}`}>{value}</div>
    <div className="text-[11px] text-ink-muted mt-0.5">{label}</div>
  </div>
);

export const NewsroomAnalyticsTab = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/news/analytics", { params: { days: 30 } }).then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (!data) return <div className="py-16 text-center text-sm text-ink-muted">Loading analytics…</div>;
  const t = data.totals || {};
  const by = t.by_status || {};
  const pipeline = [
    ["Draft", by.draft || 0, "bg-violet-500"],
    ["Approved", by.approved || 0, "bg-sky-500"],
    ["Published", by.published || 0, "bg-emerald-500"],
    ["Archived", by.archived || 0, "bg-zinc-500"],
  ];
  const maxPipe = Math.max(...pipeline.map(([, v]) => v), 1);
  const genres = (data.top_genres || []).slice(0, 8).map((g) => ({ name: g.genre, count: g.count }));

  const recs = [];
  if ((by.draft || 0) > 0) recs.push(`Generate radio scripts for ${by.draft} pending stories to maximize reach`);
  if ((by.published || 0) === 0) recs.push("Approve and publish your first story to light up your public blog & RSS feed");
  if ((t.anchor_mixes || 0) === 0) recs.push("Render your first audio bulletin in the AI Anchor tab");
  if (data.best_hour !== null && data.best_hour !== undefined) recs.push(`Your busiest auto-post hour is ${data.best_hour}:00 — schedule bulletins around it`);
  if ((t.auto_posts || 0) === 0) recs.push("Turn on social auto-posting so approved stories reach your followers automatically");

  return (
    <div data-testid="newsroom-analytics-tab">
      <h2 className="font-display text-xl font-bold text-ink-primary">News Analytics</h2>
      <p className="text-xs text-ink-muted mb-6">Content performance · Story breakdown · Publishing status (last 30 days)</p>

      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Stat value={t.stories || 0} label="Total Stories" />
        <Stat value={t.in_window || 0} label="New (30d)" tint="text-sky-300" />
        <Stat value={t.anchor_mixes || 0} label="Audio Bulletins" tint="text-violet-300" />
        <Stat value={t.videos || 0} label="News Videos" tint="text-pink-300" />
        <Stat value={t.auto_posts || 0} label="Social Posts" tint="text-amber-300" />
        <Stat value={by.published || 0} label="Published" tint="text-emerald-400" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="rounded-xl border border-line bg-bg-surface p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-pink-400" />
            <span className="text-sm font-bold text-ink-primary">Stories by Genre</span>
          </div>
          {genres.length === 0 ? (
            <div className="text-xs text-ink-muted py-10 text-center">No genre data yet — fetch some music news first.</div>
          ) : (
            <div style={{ width: "100%", height: 230 }}>
              <ResponsiveContainer>
                <BarChart data={genres}>
                  <XAxis dataKey="name" stroke="#8a8276" fontSize={10} />
                  <YAxis stroke="#8a8276" fontSize={10} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#171310", border: "1px solid #2b241d", borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: "#f5f1ea" }} formatter={(v) => [v, "Stories"]} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                    {genres.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="space-y-5">
          <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid="analytics-pipeline">
            <div className="text-sm font-bold text-ink-primary mb-4">Publishing Pipeline</div>
            <div className="space-y-3">
              {pipeline.map(([label, v, color]) => (
                <div key={label} className="flex items-center gap-3 text-xs">
                  <span className="w-20 text-ink-secondary">{label}</span>
                  <div className="flex-1 h-2 rounded-full bg-bg-elevated overflow-hidden">
                    <div className={`h-full rounded-full ${color}`} style={{ width: `${(v / maxPipe) * 100}%` }} />
                  </div>
                  <span className="font-mono font-bold text-ink-primary w-8 text-right">{v}</span>
                </div>
              ))}
            </div>
          </div>

          {recs.length > 0 && (
            <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid="analytics-recommendations">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="h-4 w-4 text-brand-amber" />
                <span className="text-sm font-bold text-ink-primary">Recommendations</span>
              </div>
              <ol className="space-y-2">
                {recs.slice(0, 4).map((r, i) => (
                  <li key={i} className="flex gap-2 text-xs text-ink-secondary">
                    <span className="font-mono font-bold text-brand-amber">{i + 1}.</span> {r}
                  </li>
                ))}
              </ol>
              <div className="mt-3 text-[9px] font-mono uppercase tracking-wider text-ink-muted">Computed from your real numbers</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
